
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/base/Button';

export default function NewsletterSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const sectionRef = useRef<HTMLElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d3kge7765j0ju67vv43g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="newsletter" ref={sectionRef} className="py-24 lg:py-32 bg-[#F5F1E6]/30">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Header */}
          <h2 
            className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#2A2A2A] mb-6"
            style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
          >
            {t('newsletter_title')}
          </h2>
          
          <p 
            className="text-xs sm:text-xs lg:text-base text-[#A6A6A6] mb-12 max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
          >
            {t('newsletter_description')}
          </p>

          {/* Newsletter Form */}
          <form 
            onSubmit={handleSubmit}
            className="max-w-md mx-auto"
            data-readdy-form
            id="newsletter-form"
          >
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('email_placeholder')}
                required
                className="flex-1 px-4 py-3 text-xs border border-[#A6A6A6] rounded-lg focus:outline-none focus:border-[#2A2A2A] transition-colors"
                style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                disabled={isSubmitting}
              />
              <Button 
                type="submit" 
                size="lg"
                disabled={isSubmitting || !email.trim()}
                className="px-6 py-3 text-xs"
              >
                {isSubmitting ? t('subscribing') : t('subscribe')}
              </Button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <p className="mt-4 text-green-600 text-xs" style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}>
                {t('subscribe_success')}
              </p>
            )}
            
            {submitStatus === 'error' && (
              <p className="mt-4 text-red-600 text-xs" style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}>
                {t('subscribe_error')}
              </p>
            )}
          </form>

          {/* Privacy Notice */}
          <p 
            className="text-xs text-[#A6A6A6] mt-6"
            style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
          >
            {t('privacy_notice')}
          </p>
        </div>
      </div>
    </section>
  );
}
